import React, { useState, useEffect } from 'react';
import { useTheme, getT, getBrandColors} from '../ThemeContext';
import API_BASE from '../apiConfig';
import { FaTrophy, FaCheckCircle, FaExclamationCircle, FaCreditCard, FaLock } from 'react-icons/fa';
import { useSort } from '../hooks/useSort';
import { useDateTimeFormat } from '../utils/formatters';

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";

const STATUS = {
  REGISTERED: { bg: '#fef9c3', text: '#854d0e', label: 'To Confirm' },
  APPROVED: { bg: '#dbeafe', text: '#1d4ed8', label: 'Confirmed' },
  PAID: { bg: '#dcfce7', text: '#166534', label: 'Paid' },
  VOIDED: { bg: '#fee2e2', text: '#991b1b', label: 'Voided' },
  CANCELLED: { bg: '#fee2e2', text: '#991b1b', label: 'Cancelled' },
};
// A parent can still complete payment while an admin-scheduled registration is
// unpaid — whether it is REGISTERED (not yet acknowledged) or APPROVED (confirmed
// but the charge never completed). Without APPROVED here, an approved-but-unpaid
// row falls out of the Confirm & Pay queue and the parent is stuck.
const needsParentPayment = (r) =>
  r.booked_by_type === 'admin' &&
  r.payment_status !== 'COMPLETED' &&
  ['REGISTERED', 'APPROVED'].includes(r.registration_status);

const TournamentRegistration = ({ currentUser }) => {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtDate } = useDateTimeFormat();
  const parentId = currentUser?.parent_id;

  const [view, setView] = useState('confirm'); // 'confirm' | 'register' | 'history'
  const [tournaments, setTournaments] = useState([]);
  const [students, setStudents] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [card, setCard] = useState(null);

  const [tournamentId, setTournamentId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [form, setForm] = useState(null);
  const [ageCategoryId, setAgeCategoryId] = useState('');
  const [selectedDateIds, setSelectedDateIds] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [ackBusyId, setAckBusyId] = useState(null);
  const [confirmSel, setConfirmSel] = useState({});

  useEffect(() => { if (parentId) init(); }, [parentId]); // eslint-disable-line

  // Lightweight refresh of just the registrations queue (used after every action).
  // Kept separate from the heavier eligible/card calls so a slow eligible fetch
  // can never block the queue from updating.
  const loadMyRegistrations = async () => {
    try {
      const res = await fetch(`${BASE}/tournament-registration/my-registrations/${parentId}`);
      const data = await res.json();
      if (!data.success) return;
      const regs = data.registrations || [];
      setMyRegistrations(regs);
      setConfirmSel(prev => {
        const next = {};
        regs.forEach(r => {
          if (needsParentPayment(r)) {
            // keep the parent's current day selection if they've touched this row
            next[r.registration_id] = prev[r.registration_id] !== undefined ? prev[r.registration_id] : (r.selected_date_ids || []);
          }
        });
        return next;
      });
    } catch (err) { console.error('registrations refresh failed', err); }
  };

  const init = async () => {
    // Each call is independent so one failing/slow endpoint can't block the others.
    loadMyRegistrations();
    try {
      const res = await fetch(`${BASE}/oto/parent-payment-method/${parentId}`);
      const data = await res.json();
      if (data.success) setCard(data.card);
    } catch (err) { console.error(err); }
    try {
      const res = await fetch(`${BASE}/tournament-registration/eligible/${parentId}`);
      const data = await res.json();
      if (data.success) { setTournaments(data.tournaments || []); setStudents(data.students || []); }
    } catch (err) { console.error(err); }
  };

  // ── Self-registration ──
  const loadForm = async (tId, sId) => {
    setError(''); setSuccess('');
    setAgeCategoryId(''); setSelectedDateIds([]); setTermsAccepted(false);
    if (!tId || !sId) { setForm(null); return; }
    try {
      const res = await fetch(`${BASE}/tournament-registration/${tId}/form/${sId}`);
      const data = await res.json();
      if (data.success) {
        setForm(data);
        if (data.computed_age_category) setAgeCategoryId(String(data.computed_age_category.age_category_id));
      } else setError(data.message || 'Failed to load registration form');
    } catch (err) { setError(err.message); }
  };
  const onTournamentChange = (id) => { setTournamentId(id); loadForm(id, studentId); };
  const onStudentChange = (id) => { setStudentId(id); loadForm(tournamentId, id); };
  const toggleDate = (dateId) => setSelectedDateIds(prev => prev.includes(dateId) ? prev.filter(id => id !== dateId) : [...prev, dateId]);

  const selectedCost = form?.age_categories.find(c => c.age_category_id === Number(ageCategoryId));
  const costPerGame = selectedCost ? Number(selectedCost.tournament_cost_per_player) : 0;
  const totalCost = costPerGame * selectedDateIds.length;
  const alreadyRegistered = form?.already_registered_category_ids.includes(Number(ageCategoryId));

  const authorizePayment = async () => {
    if (!ageCategoryId) { setError('Select an age category'); return; }
    if (selectedDateIds.length === 0) { setError('Select at least one availability day'); return; }
    if (!termsAccepted) { setError('You must accept the terms to authorize payment'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE}/tournament-registration/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tournament_id: Number(tournamentId), student_id: Number(studentId), age_category_id: Number(ageCategoryId), parent_id: parentId, date_ids: selectedDateIds, terms_accepted: true }),
      });
      const data = await res.json();
      if (data.success) { setSuccess(data.message || 'Registration submitted'); setTournamentId(''); setStudentId(''); setForm(null); init(); }
      else setError(data.message || data.detail || 'Registration failed');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  // ── Confirm & Pay ──
  const toggleConfirmDay = (regId, dateId) => {
    setConfirmSel(prev => {
      const cur = prev[regId] || [];
      const next = cur.includes(dateId) ? cur.filter(d => d !== dateId) : [...cur, dateId];
      return { ...prev, [regId]: next };
    });
  };

  const confirmOne = async (reg) => {
    const dateIds = confirmSel[reg.registration_id] || [];
    if (dateIds.length === 0) return { ok: false, msg: 'No days selected' };
    const res = await fetch(`${BASE}/tournament-registration/${reg.registration_id}/confirm-pay`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parent_id: parentId, date_ids: dateIds }),
    });
    const data = await res.json();
    return { ok: !!data.success, msg: data.message || data.detail || 'Failed' };
  };

  // Optimistically remove a registration from the pending queue immediately.
  const dropFromQueue = (registrationId, newStatus, newPayment) => {
    setMyRegistrations(prev => prev.map(x =>
      x.registration_id === registrationId
        ? { ...x, registration_status: newStatus, payment_status: newPayment || x.payment_status }
        : x));
  };

  const confirmAndPay = async (reg) => {
    if ((confirmSel[reg.registration_id] || []).length === 0) { setError('Select at least one day your child is available'); return; }
    if (!card) { setError('Add a payment method under "My Child" → Payment Method before confirming'); return; }
    setAckBusyId(reg.registration_id); setError(''); setSuccess('');
    try {
      const r = await confirmOne(reg);
      if (r.ok || /already paid/i.test(r.msg)) {
        setSuccess(r.ok ? 'Confirmed and paid — moved to My Registrations' : 'Already paid — moved to My Registrations');
        dropFromQueue(reg.registration_id, 'PAID', 'COMPLETED');   // instant soft refresh
      } else {
        setError(r.msg);
      }
    } catch (err) { setError(err.message); }
    finally { setAckBusyId(null); loadMyRegistrations(); }          // background reconcile
  };

  const confirmAndPayAll = async () => {
    if (!card) { setError('Add a payment method under "My Child" → Payment Method before confirming'); return; }
    const ready = pending.filter(r => (confirmSel[r.registration_id] || []).length > 0);
    if (ready.length === 0) { setError('Select at least one day for at least one registration'); return; }
    setAckBusyId('ALL'); setError(''); setSuccess('');
    let ok = 0, fail = 0;
    for (const reg of ready) {
      try {
        const r = await confirmOne(reg);
        if (r.ok || /already paid/i.test(r.msg)) { ok++; dropFromQueue(reg.registration_id, 'PAID', 'COMPLETED'); }
        else fail++;
      } catch { fail++; }
    }
    setAckBusyId(null);
    setSuccess(`${ok} confirmed & paid${fail ? `, ${fail} could not be charged` : ''}`);
    loadMyRegistrations();
  };

  const declineRegistration = async (reg) => {
    if (!window.confirm(`Confirm that ${reg.first_name} ${reg.last_name} will NOT participate in ${reg.tournament_name}? The academy will be notified.`)) return;
    setAckBusyId(reg.registration_id); setError(''); setSuccess('');
    try {
      const res = await fetch(`${BASE}/tournament-registration/${reg.registration_id}/decline`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parent_id: parentId }),
      });
      const data = await res.json();
      if (data.success) { setSuccess(data.message || 'Marked as not attending'); dropFromQueue(reg.registration_id, 'CANCELLED'); }
      else setError(data.message || data.detail || 'Could not update');
    } catch (err) { setError(err.message); }
    finally { setAckBusyId(null); loadMyRegistrations(); }
  };

  const pending = myRegistrations.filter(needsParentPayment);

  // Standard rule: every table column is sortable — click to sort, click again to flip.
  const pendingSort = useSort(pending, "first_name");
  const historySort = useSort(myRegistrations, "first_name");

  // ── Styles (Free Trials look) ──
  const accent = isDark ? '#93c5fd' : getBrandColors().blue;
  const panelBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)';
  const panelBdr = isDark ? '1.5px solid rgba(255,255,255,0.10)' : '1.5px solid rgba(3,78,123,0.10)';
  const softBg = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc';
  const rowBdr = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';
  const inputStyle = { border: `1.5px solid ${T.inputBdr}`, borderRadius: '8px', padding: '9px 12px', background: T.inputBg, width: '100%', boxSizing: 'border-box', outline: 'none', fontSize: '13px', color: T.inputText, fontFamily: FONT };
  const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textLabel, fontFamily: FONT };
  const th = { padding: '9px 10px', fontWeight: 800, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: FONT, color: T.textSecondary, whiteSpace: 'nowrap' };
  const td = { padding: '9px 10px', fontSize: '12.5px', fontFamily: FONT, color: T.textPrimary, verticalAlign: 'middle' };
  const dayChip = (on) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 7, fontSize: 11, fontWeight: 700,
    cursor: 'pointer', fontFamily: FONT, marginRight: 5, marginBottom: 4,
    border: `1.5px solid ${on ? getBrandColors().yellow : rowBdr}`, background: on ? 'rgba(247,206,11,0.16)' : 'transparent', color: on ? (isDark ? getBrandColors().yellow : '#8a6d00') : T.textSecondary,
  });
  // Match the Tournament Hub tab look: yellow pill when active, glass + hairline when not.
  const tabBtn = (key, label) => {
    const active = view === key;
    return (
      <button key={key} onClick={() => setView(key)} style={{
        padding: '9px 16px', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', fontFamily: FONT,
        background: active ? getBrandColors().yellow : T.glass,
        color: active ? getBrandColors().blueDark : T.textLabel,
        border: active ? 'none' : `1px solid ${T.inputBdr}`,
        boxShadow: active ? '0 4px 14px rgba(247,206,11,0.35)' : 'none',
      }}>{label}</button>
    );
  };
  const payBtn = (disabled) => ({
    padding: '7px 14px', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
    background: disabled ? (isDark ? 'rgba(255,255,255,0.10)' : '#e2e8f0') : getBrandColors().yellow, color: disabled ? T.textMuted : getBrandColors().blueDark, fontFamily: FONT,
  });

  return (
    <div style={{ padding: '22px 26px 40px', fontFamily: FONT, maxWidth: 1160 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: accent, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FaTrophy style={{ fontSize: 20 }} /> Tournaments
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: T.textSecondary }}>
            Confirm the days your child can play and pay in one place.
          </p>
        </div>
        {card ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderRadius: 9, background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.28)' }}>
            <FaCreditCard size={12} color="#10b981" />
            <span style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary }}>{(card.brand || 'Card').toUpperCase()} •••• {card.last4}</span>
          </div>
        ) : (
          <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309', padding: '7px 12px', borderRadius: 9, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.30)' }}>
            No card on file — add one under My Child → Payment Method
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {tabBtn('confirm', `✅ Confirm & Pay${pending.length ? ` (${pending.length})` : ''}`)}
        {tabBtn('register', '➕ Register')}
        {tabBtn('history', 'My Registrations')}
      </div>

      {error && (
        <div style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 9, marginBottom: 14, fontSize: 12.5, fontWeight: 600, background: 'rgba(248,113,113,0.12)', color: '#ef4444', border: '1px solid rgba(248,113,113,0.30)' }}>
          <FaExclamationCircle size={14} /><span>{error}</span>
        </div>
      )}
      {success && (
        <div style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 9, marginBottom: 14, fontSize: 12.5, fontWeight: 600, background: 'rgba(52,211,153,0.12)', color: getBrandColors().green, border: '1px solid rgba(52,211,153,0.30)' }}>
          <FaCheckCircle size={14} /><span>{success}</span>
        </div>
      )}

      {/* Panel */}
      <div style={{ background: panelBg, border: panelBdr, borderRadius: 16, padding: '18px 20px', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)' }}>

        {/* ── CONFIRM & PAY QUEUE ── */}
        {view === 'confirm' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary }}>Awaiting your confirmation</span>
              {pending.length > 0 && (
                <button onClick={confirmAndPayAll} disabled={!card || ackBusyId === 'ALL'} style={{ ...payBtn(!card || ackBusyId === 'ALL'), padding: '8px 16px', fontSize: 13 }}>
                  {ackBusyId === 'ALL' ? 'Processing…' : `Confirm & Pay All (${pending.length})`}
                </button>
              )}
            </div>

            {pending.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '36px 20px', color: T.textSecondary, fontSize: 13 }}>
                Nothing to confirm right now. When the coaching staff schedules your child, it’ll appear here.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${rowBdr}` }}>
                      <th style={{ ...th, textAlign: 'left', ...pendingSort.thSort('first_name', T) }} onClick={() => pendingSort.toggleSort('first_name')}>Student{pendingSort.sortArrow('first_name')}</th>
                      <th style={{ ...th, textAlign: 'left', ...pendingSort.thSort('tournament_name', T) }} onClick={() => pendingSort.toggleSort('tournament_name')}>Tournament{pendingSort.sortArrow('tournament_name')}</th>
                      <th style={{ ...th, textAlign: 'left', ...pendingSort.thSort('category_name', T) }} onClick={() => pendingSort.toggleSort('category_name')}>Category{pendingSort.sortArrow('category_name')}</th>
                      <th style={{ ...th, textAlign: 'left' }}>Select days available</th>
                      <th style={{ ...th, textAlign: 'right' }}>Total</th>
                      <th style={{ ...th, textAlign: 'center' }}>Status</th>
                      <th style={{ ...th, textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingSort.sorted.map((r, i) => {
                      const sel = confirmSel[r.registration_id] || [];
                      const perGame = Number(r.cost_per_game) || 0;
                      const total = perGame * sel.length;
                      return (
                        <tr key={r.registration_id} style={{ background: i % 2 ? softBg : 'transparent', borderBottom: `1px solid ${rowBdr}` }}>
                          <td style={{ ...td, fontWeight: 700 }}>{r.first_name} {r.last_name}</td>
                          <td style={td}>{r.tournament_name}</td>
                          <td style={td}>{r.category_name}</td>
                          <td style={{ ...td, minWidth: 240 }}>
                            {(r.tournament_dates || []).map(d => {
                              const on = sel.includes(d.tournament_date_id);
                              return (
                                <span key={d.tournament_date_id} onClick={() => toggleConfirmDay(r.registration_id, d.tournament_date_id)} style={dayChip(on)}>
                                  {on ? '✓ ' : ''}D{d.day_number} · {fmtDate(d.tournament_date)}
                                </span>
                              );
                            })}
                            <div style={{ fontSize: 10.5, color: T.textMuted, marginTop: 2 }}>${perGame.toFixed(2)}/day · {sel.length} selected</div>
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 800, fontSize: 14 }}>${total.toFixed(2)}</td>
                          <td style={{ ...td, textAlign: 'center' }}>
                            <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#fef9c3', color: '#854d0e', whiteSpace: 'nowrap' }}>
                              Awaiting Payment
                            </span>
                          </td>
                          <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                            <button onClick={() => confirmAndPay(r)} disabled={!card || sel.length === 0 || ackBusyId === r.registration_id} style={{ ...payBtn(!card || sel.length === 0 || ackBusyId === r.registration_id), marginRight: 6 }}>
                              {ackBusyId === r.registration_id ? '…' : 'Confirm & Pay'}
                            </button>
                            <button onClick={() => declineRegistration(r)} disabled={ackBusyId === r.registration_id}
                              style={{ padding: '7px 12px', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: ackBusyId === r.registration_id ? 'default' : 'pointer', background: 'transparent', color: '#ef4444', fontFamily: FONT }}>
                              Can’t Attend
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── REGISTER (self-service) ── */}
        {view === 'register' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Tournament</label>
                <select value={tournamentId} onChange={(e) => onTournamentChange(e.target.value)} style={inputStyle}>
                  <option value="">— Choose —</option>
                  {tournaments.map(t => <option key={t.tournament_id} value={t.tournament_id}>{t.tournament_name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Student</label>
                <select value={studentId} onChange={(e) => onStudentChange(e.target.value)} style={inputStyle}>
                  <option value="">— Choose —</option>
                  {students.map(s => <option key={s.student_id} value={s.student_id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
            </div>
            {tournaments.length === 0 && <div style={{ fontSize: 12.5, color: T.textSecondary }}>No open tournaments available for registration right now.</div>}

            {form && (
              <>
                <label style={labelStyle}>Age Category</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                  {form.age_categories.map(c => {
                    const dis = form.already_registered_category_ids.includes(c.age_category_id);
                    const on = ageCategoryId === String(c.age_category_id);
                    return (
                      <button key={c.age_category_id} disabled={dis} onClick={() => setAgeCategoryId(String(c.age_category_id))} style={{
                        padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: dis ? 'not-allowed' : 'pointer', opacity: dis ? 0.5 : 1, fontFamily: FONT,
                        border: `2px solid ${on ? getBrandColors().yellow : T.inputBdr}`, background: on ? 'rgba(247,206,11,0.15)' : 'transparent', color: on ? (isDark ? getBrandColors().yellow : '#8a6d00') : T.textSecondary,
                      }}>{c.category_name}{dis ? ' (registered)' : ''}</button>
                    );
                  })}
                </div>

                <label style={labelStyle}>Availability</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 16 }}>
                  {form.dates.map(d => {
                    const on = selectedDateIds.includes(d.tournament_date_id);
                    return (
                      <span key={d.tournament_date_id} onClick={() => toggleDate(d.tournament_date_id)} style={dayChip(on)}>
                        {on ? '✓ ' : ''}Day {d.day_number} · {fmtDate(d.tournament_date)}
                      </span>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: 10, background: softBg, marginBottom: 14 }}>
                  <div style={{ fontSize: 12.5, color: T.textSecondary }}>{selectedDateIds.length} day(s) × ${costPerGame.toFixed(2)}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.textPrimary }}>${totalCost.toFixed(2)}</div>
                </div>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14, fontSize: 12, color: T.textSecondary, cursor: 'pointer' }}>
                  <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} style={{ marginTop: 2 }} />
                  I authorize Sixers Sports Center to charge my card on file for the total above and accept the tournament terms.
                </label>

                <button onClick={authorizePayment} disabled={loading || !card || alreadyRegistered}
                  style={{ width: '100%', padding: '12px', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer', backgroundColor: getBrandColors().yellow, color: getBrandColors().blueDark, fontFamily: FONT, opacity: (loading || !card || alreadyRegistered) ? 0.5 : 1 }}>
                  {loading ? 'Processing…' : `Register & Pay $${totalCost.toFixed(2)}`}
                </button>
              </>
            )}
          </div>
        )}

        {/* ── HISTORY ── */}
        {view === 'history' && (
          myRegistrations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 20px', color: T.textSecondary, fontSize: 13 }}>No registrations yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${rowBdr}` }}>
                    <th style={{ ...th, textAlign: 'left', ...historySort.thSort('first_name', T) }} onClick={() => historySort.toggleSort('first_name')}>Student{historySort.sortArrow('first_name')}</th>
                    <th style={{ ...th, textAlign: 'left', ...historySort.thSort('tournament_name', T) }} onClick={() => historySort.toggleSort('tournament_name')}>Tournament{historySort.sortArrow('tournament_name')}</th>
                    <th style={{ ...th, textAlign: 'left', ...historySort.thSort('category_name', T) }} onClick={() => historySort.toggleSort('category_name')}>Category{historySort.sortArrow('category_name')}</th>
                    <th style={{ ...th, textAlign: 'center', ...historySort.thSort('number_of_days_selected', T) }} onClick={() => historySort.toggleSort('number_of_days_selected')}>Days{historySort.sortArrow('number_of_days_selected')}</th>
                    <th style={{ ...th, textAlign: 'right', ...historySort.thSort('total_cost', T) }} onClick={() => historySort.toggleSort('total_cost')}>Total{historySort.sortArrow('total_cost')}</th>
                    <th style={{ ...th, textAlign: 'center', ...historySort.thSort('registration_status', T) }} onClick={() => historySort.toggleSort('registration_status')}>Status{historySort.sortArrow('registration_status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {historySort.sorted.map((r, i) => {
                    const s = STATUS[r.registration_status] || STATUS.REGISTERED;
                    return (
                      <tr key={r.registration_id} style={{ background: i % 2 ? softBg : 'transparent', borderBottom: `1px solid ${rowBdr}` }}>
                        <td style={{ ...td, fontWeight: 700 }}>{r.first_name} {r.last_name}</td>
                        <td style={td}>{r.tournament_name}</td>
                        <td style={td}>{r.category_name}</td>
                        <td style={{ ...td, textAlign: 'center' }}>{r.number_of_days_selected}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>${Number(r.total_cost).toFixed(2)}</td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.text }}>{s.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default TournamentRegistration;
